(function () {
  'use strict';
  if (window.EFLeadCapture) { window.EFLeadCapture.initialize(); return; }
  const PREFIX = 'ef_lead_';
  const VISIT_TTL = 30 * 60 * 1000;
  const DISMISS_TTL = 30 * 24 * 60 * 60 * 1000;
  // Accessing the storage property itself can throw when browser storage is blocked.
  const read = (storage, key) => { try { return window[storage].getItem(PREFIX + key); } catch (_) { return null; } };
  const write = (storage, key, value) => { try { window[storage].setItem(PREFIX + key, value); return true; } catch (_) { return false; } };
  const roots = () => Array.from(document.querySelectorAll('[data-lead-capture]'));
  const nativeRoots = () => roots().filter(root => root.dataset.preview !== 'true');
  const cookies = () => document.cookie.split(';').map(part => part.trim().split('=')[0]);
  const subscribed = () => read('localStorage', 'subscribed') === '1';
  const formIDs = () => new Set(nativeRoots().flatMap(root => [
    root.dataset.popupForm,
    ...Array.from(root.querySelectorAll('[data-lead-native]')).map(mount => mount.id.replace('omnisend-embedded-v2-', ''))
  ]).filter(Boolean));
  let visit;
  try { visit = JSON.parse(read('sessionStorage', 'visit') || 'null'); } catch (_) {}
  const now = Date.now();
  if (!visit || !Number.isFinite(visit.lastAt) || now - visit.lastAt > VISIT_TTL) {
    visit = { activeMs: 0, views: 0, shown: false, suppressed: false, lastAt: now };
  }
  // A policy update must not reopen a popup already seen or a shopping visit
  // already protected by an earlier controller.
  visit.activeMs = Number.isFinite(visit.activeMs) ? Math.max(0, visit.activeMs) : 0;
  visit.views = Number.isFinite(visit.views) ? Math.max(0, Math.floor(visit.views)) : 0;
  visit.startedAt = Number.isFinite(visit.startedAt) ? visit.startedAt : now;
  visit.triggerVersion = 4;
  visit.views += 1;
  visit.lastAt = now;
  const saveVisit = () => write('sessionStorage', 'visit', JSON.stringify(visit));
  let storageAvailable = saveVisit();
  let lastTick = now;
  let lastInput = now;
  let pageActiveMs = 0;
  let pageSuspended = false;
  let scrolledHalf = false;
  let exitIntent = false;
  let pendingOpenAt = 0;
  let lastAttemptAt = 0;
  let openAttempts = 0;
  let opening = false;
  let timer;
  let opener;
  const suppressVisit = () => { visit.suppressed = true; saveVisit(); };
  const sourceIsEmail = () => {
    const params = new URLSearchParams(window.location.search);
    return Array.from(params.entries()).some(([key, value]) =>
      /^(omnisend|soundest)/i.test(key) ||
      (/^utm_(source|medium)$/i.test(key) && /omnisend|soundest|email|sms/i.test(value)));
  };
  if (sourceIsEmail()) suppressVisit();

  function rememberSubscriber() {
    write('localStorage', 'subscribed', '1');
    suppressVisit();
    // Recognition blocks the popup; embedded signup sections and their native
    // success/already-subscribed screens remain available to every visitor.
  }

  function initialize() {
    roots().forEach(root => {
      if (root.dataset.initialized) return;
      root.dataset.initialized = 'true';
      const preview = root.dataset.preview === 'true';
      const dialog = root.querySelector('dialog');
      let previewOpener;
      let previousOverflow;
      if (dialog) {
        const close = () => { if (dialog.open) dialog.close(); };
        dialog.querySelectorAll('[data-lead-close]').forEach(button => button.addEventListener('click', close));
        dialog.addEventListener('click', event => {
          const rect = dialog.getBoundingClientRect();
          if (event.target === dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) close();
        });
        dialog.addEventListener('close', () => {
          document.body.style.overflow = previousOverflow || '';
          if (previewOpener?.isConnected) previewOpener.focus({ preventScroll: true });
        });
        if (preview && new URLSearchParams(window.location.search).get('ef_popup_preview') === '1') {
          previewOpener = root.querySelector('[data-lead-preview-submit]');
          previousOverflow = document.body.style.overflow;
          dialog.showModal();
          document.body.style.overflow = 'hidden';
          dialog.querySelector('[data-lead-close]').focus();
        }
      }
      root.querySelectorAll('[data-lead-preview-submit]').forEach(button => button.addEventListener('click', () => {
        const form = button.closest('[data-lead-preview-form]');
        if (!form.querySelector('input').reportValidity()) return;
        const status = form.querySelector('[data-lead-status]');
        status.hidden = false;
        status.focus({ preventScroll: true });
      }));
      if (preview) return;
      const mount = root.querySelector('[data-lead-native]');
      if (mount && root.querySelector('[data-lead-subscribed]')) {
        rememberSubscriber();
      }
    });
    if (!timer && nativeRoots().some(root => root.dataset.popupEnabled === 'true')) {
      timer = window.setInterval(tick, 1000);
    }
  }

  function popupRoot() {
    return nativeRoots().find(root => !root.hidden && root.dataset.popupEnabled === 'true' && root.dataset.popupForm);
  }

  function triggerReady() {
    // Landing pages stay free of automatic popups, including desktop exit.
    // Deliberate signup actions bypass this automatic engagement gate.
    return visit.views >= 2 && visit.activeMs >= 45000 && pageActiveMs >= 15000 &&
      (scrolledHalf || exitIntent);
  }

  function visible(element) {
    if (!element) return false;
    const style = window.getComputedStyle(element);
    return element.getClientRects().length > 0 && style.visibility !== 'hidden' &&
      style.visibility !== 'collapse' && style.opacity !== '0';
  }

  function blockReason(root, automatic) {
    if (!root) return 'no-eligible-popup';
    if (root.dataset.popupEligible === 'false') return 'excluded-page';
    if (root.dataset.designMode === 'true') return 'theme-editor';
    if (subscribed()) return 'subscribed';
    if (pageSuspended || document.hidden || !document.hasFocus()) return 'page-inactive';
    // The popup is independent of whether an embedded form has hydrated.
    if (typeof window.omnisend?.push !== 'function') return 'sdk-unavailable';
    if (typeof window.Shopify?.customerPrivacy?.marketingAllowed === 'function' && !window.Shopify.customerPrivacy.marketingAllowed()) return 'marketing-consent';
    const active = document.activeElement;
    if (active && (active.matches('input,textarea,select,[contenteditable="true"]'))) return 'input-focused';
    if (Array.from(document.querySelectorAll('dialog[open],[role="dialog"][aria-modal="true"],#NotificationSuccess')).some(visible)) return 'dialog-open';
    if (document.documentElement.classList.contains('js-drawer-open') || document.body.classList.contains('js-drawer-open')) return 'drawer-open';
    if (document.body.classList.contains('ef-collf-lock') || document.querySelector('[data-ef-collf].is-filter-open')) return 'drawer-open';
    if (visible(document.querySelector('#filter-finder.product-search__container--show'))) return 'finder-open';
    if (!automatic) return null;
    if (!storageAvailable) return 'storage-unavailable';
    if (root.dataset.popupAuto !== 'true' || root.dataset.popupEligible !== 'true') return 'automatic-disabled';
    if (root.dataset.knownCustomer === 'true' || cookies().includes('omnisendContactID')) return 'known-contact';
    if (cookies().some(name => name.startsWith('omnisend-form-') && name.endsWith('-filled-at'))) return 'native-subscriber';
    const dismissed = Number(read('localStorage', 'dismissed_at'));
    if (dismissed > 0 && Date.now() - dismissed < DISMISS_TTL) return 'dismissed';
    // Fail closed for a native dismissal made before this controller was installed.
    if (!dismissed && cookies().includes('omnisend-form-' + root.dataset.popupForm + '-closed-at')) return 'native-dismissal';
    if (visit.shown) return 'already-shown';
    if (visit.suppressed) return 'visit-suppressed';
    if (!triggerReady()) return 'waiting-for-trigger';
    if (Date.now() - lastInput < 5000) return 'interaction-pause';
    return null;
  }

  function blocked(root, automatic) {
    return blockReason(root, automatic) !== null;
  }

  function confirmView() {
    pendingOpenAt = 0;
    visit.shown = true;
    saveVisit();
  }

  async function openNative(automatic) {
    const root = popupRoot();
    if (opening || pendingOpenAt || blocked(root, automatic)) return false;
    if (automatic && (openAttempts >= 3 || (lastAttemptAt && Date.now() - lastAttemptAt < 10000))) return false;
    opening = true;
    const abort = new AbortController();
    const timeout = window.setTimeout(() => abort.abort(), 3000);
    try {
      const route = (window.Shopify?.routes?.root || '/') + 'cart.js';
      const response = await fetch(route, { cache: 'no-store', headers: { Accept: 'application/json' }, signal: abort.signal });
      if (!response.ok) return false;
      const cart = await response.json();
      if (!Number.isFinite(cart.item_count)) return false;
      if (cart.item_count > 0) { suppressVisit(); return false; }
      // State can change while the cart request is pending.
      if (blocked(root, automatic)) return false;
      opener = document.activeElement;
      // A queued SDK request is not proof that the visitor saw the form.
      pendingOpenAt = Date.now();
      lastAttemptAt = pendingOpenAt;
      openAttempts += 1;
      window.omnisend.push(['openForm', root.dataset.popupForm]);
      return true;
    } catch (_) {
      return false; // No popup if cart state cannot be established.
    } finally {
      window.clearTimeout(timeout);
      opening = false;
    }
  }

  function tick() {
    const time = Date.now();
    if (pageSuspended) return;
    if (!pageSuspended && !document.hidden && document.hasFocus()) {
      const elapsed = Math.max(0, Math.min(time - lastTick, 2000));
      visit.activeMs += elapsed;
      pageActiveMs += elapsed;
      visit.lastAt = time;
    }
    lastTick = time;
    storageAvailable = saveVisit();
    if (pendingOpenAt) {
      const root = popupRoot();
      const native = root && document.querySelector('.omnisend-form-' + root.dataset.popupForm + '-container');
      if (visible(native)) confirmView();
      else if (time - pendingOpenAt >= 8000) pendingOpenAt = 0;
    }
    void openNative(true);
  }

  function noteInteraction(event) {
    lastInput = Date.now();
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    // Protect a task for the entire visit once the shopper actually starts it.
    // Reading a section or following an ordinary product link is still browsing.
    const isAction = ['pointerdown', 'input', 'change'].includes(event.type) ||
      (event.type === 'keydown' && (event.key === 'Enter' || event.key === ' '));
    const control = target.closest('input,textarea,select,button,label,summary,[role="button"],[contenteditable="true"],.toggle-product-search');
    if (isAction && control && (
      control.closest('[data-ef-housing],#filter-finder,[data-filter-finder],[data-ef-collection-filters],[data-lead-native],form[action*="/cart"],form[action*="/search"]') ||
      control.matches('.toggle-product-search,[data-ef-sort],[data-ef-filter-open],[data-ef-remove],[data-ef-filter-clear],[data-ef-filter-apply],[name="checkout"],input[type="search"],input[name="q"]') ||
      (control.matches('input[type="email"]') && control.closest('form[action*="/contact"],[data-lead-capture],.omnisend-form'))
    )) suppressVisit();
    const link = target.closest('a[href]');
    if (link) {
      const url = new URL(link.href, window.location.href);
      if (isAction && url.origin === window.location.origin && /\/(cart|checkout)(\/|$)/.test(url.pathname)) suppressVisit();
    }
  }

  window.addEventListener('omnisendForms', event => {
    const detail = event.detail || {};
    const id = detail.form?.id;
    const name = detail.eventName || detail.type;
    if (!id || !formIDs().has(id)) return;
    if (name === 'submit') rememberSubscriber();
    const root = nativeRoots().find(item => item.dataset.popupForm === id);
    if (root && name === 'view') confirmView();
    if (root && name === 'close') {
      write('localStorage', 'dismissed_at', String(Date.now()));
      visit.shown = true;
      saveVisit();
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    }
  });
  window.addEventListener('storage', event => {
    if (event.key === PREFIX + 'subscribed' && event.newValue === '1') suppressVisit();
  });
  ['pointerdown', 'keydown', 'input', 'change'].forEach(name => document.addEventListener(name, noteInteraction, true));
  window.addEventListener('scroll', () => {
    lastInput = Date.now();
    const distance = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight) - window.innerHeight;
    if (distance > 0 && window.scrollY / distance >= 0.5) scrolledHalf = true;
  }, { passive: true });
  document.addEventListener('mouseout', event => {
    if (event.relatedTarget === null && event.clientY <= 0 && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      exitIntent = true;
      void openNative(true);
    }
  });
  document.addEventListener('cart:updated', suppressVisit);
  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target.closest('a[href="#ef-email-offer"],[data-lead-open-native]') : null;
    if (!target || !popupRoot()) return;
    event.preventDefault();
    void openNative(false);
  });
  document.addEventListener('shopify:section:load', initialize);
  // Background time and BFCache suspension never count as active browsing.
  const resetTick = () => { lastTick = Date.now(); };
  document.addEventListener('visibilitychange', resetTick);
  window.addEventListener('blur', resetTick);
  window.addEventListener('focus', resetTick);
  window.addEventListener('pagehide', () => { pageSuspended = true; resetTick(); saveVisit(); });
  window.addEventListener('pageshow', () => {
    const time = Date.now();
    let stored;
    try { stored = JSON.parse(read('sessionStorage', 'visit') || 'null'); } catch (_) {}
    if (stored && Number.isFinite(stored.lastAt) && time - stored.lastAt <= VISIT_TTL) {
      if (stored.startedAt === visit.startedAt) {
        // A restored page must retain the protection earned on later pages.
        visit.shown = !!(visit.shown || stored.shown);
        visit.suppressed = !!(visit.suppressed || stored.suppressed);
        visit.activeMs = Math.max(visit.activeMs, Number.isFinite(stored.activeMs) ? stored.activeMs : 0);
        visit.views = Math.max(visit.views, Number.isFinite(stored.views) ? stored.views : 0);
        visit.lastAt = Math.max(visit.lastAt, stored.lastAt);
      } else {
        // Another page started a new visit while this one was cached.
        visit = { ...stored,
          activeMs: Number.isFinite(stored.activeMs) ? Math.max(0, stored.activeMs) : 0,
          views: Number.isFinite(stored.views) ? Math.max(1, Math.floor(stored.views)) : 1,
          startedAt: Number.isFinite(stored.startedAt) ? stored.startedAt : time,
          triggerVersion: 4
        };
        pageActiveMs = 0;
        scrolledHalf = exitIntent = false;
        pendingOpenAt = lastAttemptAt = openAttempts = 0;
        lastInput = time;
      }
    } else {
      visit = { activeMs: 0, views: 1, shown: false, suppressed: false, startedAt: time, lastAt: time, triggerVersion: 4 };
      pageActiveMs = 0;
      scrolledHalf = exitIntent = false;
      pendingOpenAt = lastAttemptAt = openAttempts = 0;
      lastInput = time;
    }
    pageSuspended = false;
    resetTick();
    storageAvailable = saveVisit();
    if (sourceIsEmail()) suppressVisit();
  });
  window.EFLeadCapture = {
    initialize,
    open: () => openNative(false),
    status: () => ({
      reason: blockReason(popupRoot(), true),
      pageActiveMs, visitActiveMs: visit.activeMs, pageViews: visit.views, scrolledHalf, exitIntent,
      pending: !!pendingOpenAt, attempts: openAttempts, shown: visit.shown
    })
  };
  initialize();
})();
